<template>
    <div class="fnp">
        <div class="fnp__toolbar">
            <label class="fnp__search">
                <svg class="fnp__search-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M10.5 3a7.5 7.5 0 0 1 5.96 12.06l4.24 4.24a1 1 0 0 1-1.42 1.42l-4.24-4.24A7.5 7.5 0 1 1 10.5 3Zm0 2a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11Z"/></svg>
                <input v-model.trim="query" type="search" class="fnp__search-input"
                    placeholder="Search funnels, products and price points" aria-label="Search funnels" />
                <button v-if="query" type="button" class="fnp__search-clear" aria-label="Clear search" @click="query = ''">
                    <cross-icon />
                </button>
            </label>
            <span class="fnp__summary">
                <template v-if="chips.length === 0">All funnels</template>
                <template v-else>{{ selectedLeafCount }} {{ selectedLeafCount === 1 ? 'price point' : 'price points' }} selected</template>
            </span>
        </div>

        <div class="fnp__cols">
            <section class="fnp__col">
                <header class="fnp__col-head">
                    <span class="fnp__col-title">Funnels</span>
                    <span class="fnp__col-count">{{ funnelsList.length }}</span>
                </header>
                <div class="fnp__list" role="listbox" aria-label="Funnels">
                    <div v-for="node in funnelsList" :key="node.id" class="fnp__row"
                        :class="{ 'is-active': node.id === activeFunnelId, 'is-checked': stateOf(node) === 'checked' }"
                        @click="hasChildren(node) ? activate('funnel', node) : toggle(node)">
                        <button type="button" class="fnp__check" role="checkbox" :aria-checked="ariaChecked(node)"
                            :class="`is-${stateOf(node)}`" @click.stop="toggle(node)">
                            <check-icon v-if="stateOf(node) === 'checked'" />
                            <minus-icon v-else-if="stateOf(node) === 'indeterminate'" />
                        </button>
                        <span class="fnp__label"><span v-for="(p, i) in parts(node.label)" :key="i" :class="{ 'fnp__hit': p.hit }">{{ p.text }}</span></span>
                        <span v-if="hasChildren(node)" class="fnp__meta">{{ node.children.length }}</span>
                        <chevron-right-icon v-if="hasChildren(node)" class="fnp__chevron" />
                    </div>
                    <div v-if="funnelsList.length === 0" class="fnp__empty">No funnels match "{{ query }}"</div>
                </div>
            </section>

            <section class="fnp__col">
                <header class="fnp__col-head">
                    <span class="fnp__col-title">Products</span>
                    <span v-if="activeFunnel" class="fnp__col-sub">{{ activeFunnel.label }}</span>
                    <span class="fnp__col-count">{{ productsList.length }}</span>
                </header>
                <div class="fnp__list" role="listbox" aria-label="Products">
                    <div v-for="node in productsList" :key="node.id" class="fnp__row"
                        :class="{ 'is-active': node.id === activeProductId, 'is-checked': stateOf(node) === 'checked' }"
                        @click="hasChildren(node) ? activate('product', node) : toggle(node)">
                        <button type="button" class="fnp__check" role="checkbox" :aria-checked="ariaChecked(node)"
                            :class="`is-${stateOf(node)}`" @click.stop="toggle(node)">
                            <check-icon v-if="stateOf(node) === 'checked'" />
                            <minus-icon v-else-if="stateOf(node) === 'indeterminate'" />
                        </button>
                        <span class="fnp__label"><span v-for="(p, i) in parts(node.label)" :key="i" :class="{ 'fnp__hit': p.hit }">{{ p.text }}</span></span>
                        <span v-if="hasChildren(node)" class="fnp__meta">{{ node.children.length }}</span>
                        <chevron-right-icon v-if="hasChildren(node)" class="fnp__chevron" />
                    </div>
                    <div v-if="!activeFunnel" class="fnp__empty">Pick a funnel to see its products</div>
                    <div v-else-if="productsList.length === 0" class="fnp__empty">No products here</div>
                </div>
            </section>

            <section class="fnp__col">
                <header class="fnp__col-head">
                    <span class="fnp__col-title">Price points</span>
                    <span v-if="activeProduct" class="fnp__col-sub">{{ activeProduct.label }}</span>
                    <span class="fnp__col-count">{{ pricePointsList.length }}</span>
                </header>
                <div class="fnp__list" role="listbox" aria-label="Price points">
                    <div v-for="node in pricePointsList" :key="node.id" class="fnp__row fnp__row--leaf"
                        :class="{ 'is-checked': stateOf(node) === 'checked' }" @click="toggle(node)">
                        <button type="button" class="fnp__check" role="checkbox" :aria-checked="ariaChecked(node)"
                            :class="`is-${stateOf(node)}`" @click.stop="toggle(node)">
                            <check-icon v-if="stateOf(node) === 'checked'" />
                        </button>
                        <span class="fnp__label"><span v-for="(p, i) in parts(node.label)" :key="i" :class="{ 'fnp__hit': p.hit }">{{ p.text }}</span></span>
                    </div>
                    <div v-if="!activeProduct" class="fnp__empty">Pick a product to see its price points</div>
                    <div v-else-if="pricePointsList.length === 0" class="fnp__empty">No price points here</div>
                </div>
            </section>
        </div>

        <div v-if="chips.length" class="fnp__chips">
            <button v-for="chip in chips" :key="chip.id" type="button" class="fnp__chip" :title="chip.path"
                @click="toggle(chip.node)">
                <span class="fnp__chip-label">{{ chip.label }}</span>
                <span v-if="chip.parent" class="fnp__chip-parent">{{ chip.parent }}</span>
                <cross-icon class="fnp__chip-x" />
            </button>
            <button type="button" class="fnp__chips-clear" @click="clearAll">Clear all</button>
        </div>
    </div>
</template>
<script>
import CheckIcon from '@/assets/images/icons/CheckIcon.svg';
import MinusIcon from '@/assets/images/icons/MinusIcon.svg';
import CrossIcon from '@/assets/images/icons/CrossIcon.svg';
import ChevronRightIcon from '@/assets/images/icons/ChevronRightIcon.svg';

// Three-column browser (funnels › products › price points) over the tree the
// API returns for `transactions/filters-for-funnel`.
//
// v-model is the same array of ids the old treeselect produced: a node id is
// emitted when everything beneath it is selected, otherwise the ids of the
// selected descendants ("branch priority"). The backend expands f_/pr_ ids.
export default {
    name: 'FunnelPicker',
    components: { CheckIcon, MinusIcon, CrossIcon, ChevronRightIcon },
    props: {
        options: { type: Array, default: () => [] },
        value: { type: Array, default: () => [] },
    },
    data() {
        return {
            query: '',
            activeFunnelId: null,
            activeProductId: null,
            selectedLeaves: {},
        };
    },
    computed: {
        // id -> { node, parent, funnel } for quick lookups
        index() {
            const map = {};
            const walk = (node, parent, funnel) => {
                map[node.id] = { node, parent, funnel };
                (node.children || []).forEach((c) => walk(c, node, funnel || node));
            };
            this.options.forEach((f) => walk(f, null, null));
            return map;
        },
        lowerQuery() {
            return this.query.toLowerCase();
        },
        funnelsList() {
            return this.options.filter((f) => this.matches(f));
        },
        activeFunnel() {
            return this.activeFunnelId ? this.index[this.activeFunnelId]?.node : null;
        },
        productsList() {
            if (!this.activeFunnel) return [];
            // If the funnel itself matches the query, show all its products
            const showAll = this.selfMatches(this.activeFunnel);
            return (this.activeFunnel.children || []).filter((p) => showAll || this.matches(p));
        },
        activeProduct() {
            return this.activeProductId ? this.index[this.activeProductId]?.node : null;
        },
        pricePointsList() {
            if (!this.activeProduct) return [];
            const showAll = this.selfMatches(this.activeProduct) || (this.activeFunnel && this.selfMatches(this.activeFunnel));
            return (this.activeProduct.children || []).filter((pp) => showAll || this.matches(pp));
        },
        normalizedValue() {
            return this.normalize(this.options);
        },
        chips() {
            return this.normalizedValue.map((id) => {
                const entry = this.index[id];
                if (!entry) return { id, label: id, node: { id }, path: id };
                const { node, parent, funnel } = entry;
                const crumbs = [];
                if (funnel && funnel !== node) crumbs.push(funnel.label);
                if (parent && parent !== funnel) crumbs.push(parent.label);
                return {
                    id,
                    node,
                    label: node.label,
                    parent: crumbs.join(' › '),
                    path: [...crumbs, node.label].join(' › '),
                };
            });
        },
        selectedLeafCount() {
            return Object.keys(this.selectedLeaves).length;
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(val) {
                this.syncFromValue(val || []);
            },
        },
        options() {
            this.syncFromValue(this.value || []);
        },
        query() {
            // Keep the drill-down pointing at something visible
            if (this.activeFunnelId && !this.funnelsList.some((f) => f.id === this.activeFunnelId)) {
                this.activeFunnelId = null;
                this.activeProductId = null;
            }
            if (!this.activeFunnelId && this.lowerQuery && this.funnelsList.length) {
                this.activeFunnelId = this.funnelsList[0].id;
            }
            if (this.activeProductId && !this.productsList.some((p) => p.id === this.activeProductId)) {
                this.activeProductId = null;
            }
            if (!this.activeProductId && this.lowerQuery && this.productsList.length) {
                this.activeProductId = this.productsList[0].id;
            }
        },
    },
    methods: {
        hasChildren(node) {
            return Array.isArray(node.children) && node.children.length > 0;
        },
        leavesOf(node) {
            if (!this.hasChildren(node)) return [node.id];
            return node.children.flatMap((c) => this.leavesOf(c));
        },
        stateOf(node) {
            const leaves = this.leavesOf(node);
            const picked = leaves.filter((id) => this.selectedLeaves[id]).length;
            if (picked === 0) return 'none';
            return picked === leaves.length ? 'checked' : 'indeterminate';
        },
        ariaChecked(node) {
            const s = this.stateOf(node);
            return s === 'indeterminate' ? 'mixed' : String(s === 'checked');
        },
        selfMatches(node) {
            return !this.lowerQuery || String(node.label || '').toLowerCase().includes(this.lowerQuery);
        },
        matches(node) {
            if (this.selfMatches(node)) return true;
            return (node.children || []).some((c) => this.matches(c));
        },
        parts(label) {
            const text = String(label || '');
            if (!this.lowerQuery) return [{ text, hit: false }];
            const i = text.toLowerCase().indexOf(this.lowerQuery);
            if (i === -1) return [{ text, hit: false }];
            return [
                { text: text.slice(0, i), hit: false },
                { text: text.slice(i, i + this.lowerQuery.length), hit: true },
                { text: text.slice(i + this.lowerQuery.length), hit: false },
            ].filter((p) => p.text !== '');
        },
        activate(level, node) {
            if (level === 'funnel') {
                this.activeFunnelId = node.id;
                this.activeProductId = null;
            } else {
                this.activeProductId = node.id;
            }
        },
        toggle(node) {
            const leaves = this.leavesOf(node);
            const allOn = leaves.every((id) => this.selectedLeaves[id]);
            const next = { ...this.selectedLeaves };
            leaves.forEach((id) => {
                if (allOn) delete next[id];
                else next[id] = true;
            });
            this.selectedLeaves = next;
            this.emit();
        },
        clearAll() {
            this.selectedLeaves = {};
            this.emit();
        },
        normalize(nodes) {
            const out = [];
            nodes.forEach((node) => {
                if (!this.hasChildren(node)) {
                    if (this.selectedLeaves[node.id]) out.push(node.id);
                    return;
                }
                const state = this.stateOf(node);
                if (state === 'checked') out.push(node.id);
                else if (state === 'indeterminate') out.push(...this.normalize(node.children));
            });
            return out;
        },
        syncFromValue(ids) {
            const current = this.normalizedValue;
            if (ids.length === current.length && ids.every((id, i) => id === current[i])) return;
            const next = {};
            ids.forEach((id) => {
                const entry = this.index[id];
                if (entry) this.leavesOf(entry.node).forEach((leaf) => { next[leaf] = true; });
            });
            this.selectedLeaves = next;
        },
        emit() {
            this.$emit('input', this.normalizedValue);
        },
    },
};
</script>
<style lang="scss">
.fnp {
    display: flex;
    flex-direction: column;
    gap: 12px;

    &__toolbar {
        display: flex;
        align-items: center;
        gap: 14px;
    }

    &__search {
        position: relative;
        display: flex;
        align-items: center;
        flex: 1 1 auto;
        min-width: 0;
        height: 42px;
        padding: 0 12px 0 40px;
        border: 1px solid var(--af-border);
        border-radius: var(--af-radius);
        background: #fff;
        transition: border-color 120ms ease, box-shadow 120ms ease;

        &:focus-within {
            border-color: var(--af-primary);
            box-shadow: 0 0 0 3px var(--af-primary-ring);
        }
    }

    &__search-icon {
        position: absolute;
        left: 14px;
        width: 15px;
        height: 15px;
        fill: var(--af-faint);
        pointer-events: none;
    }

    &__search-input {
        flex: 1 1 auto;
        min-width: 0;
        border: 0;
        outline: none;
        background: transparent;
        font: inherit;
        font-size: 14px;
        color: var(--af-fg);

        &::placeholder { color: var(--af-faint); }
        &::-webkit-search-cancel-button { display: none; }
    }

    &__search-clear {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        border: 0;
        border-radius: 999px;
        background: transparent;
        color: var(--af-faint);
        cursor: pointer;

        svg { width: 14px; height: 14px; fill: currentColor; }
        &:hover { background: var(--af-surface-2); color: var(--af-fg); }
    }

    &__summary {
        flex: 0 0 auto;
        font-size: 13px;
        font-weight: 600;
        color: var(--af-muted);
        font-variant-numeric: tabular-nums;
    }

    &__cols {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        border: 1px solid var(--af-border);
        border-radius: 12px;
        overflow: hidden;
        background: #fff;
    }

    &__col {
        display: flex;
        flex-direction: column;
        min-width: 0;
        height: clamp(200px, 36vh, 320px);

        & + & { border-left: 1px solid var(--af-border); }
    }

    &__col-head {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 12px;
        border-bottom: 1px solid var(--af-border);
        background: var(--af-surface);
        font-size: 12.5px;
        min-height: 40px;
    }

    &__col-title { font-weight: 700; color: var(--af-fg); flex: 0 0 auto; }

    &__col-sub {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--af-muted);

        &::before { content: '› '; color: var(--af-faint); }
    }

    &__col-count {
        margin-left: auto;
        flex: 0 0 auto;
        padding: 1px 7px;
        border-radius: 999px;
        background: var(--af-surface-2);
        color: var(--af-muted);
        font-weight: 600;
        font-variant-numeric: tabular-nums;
    }

    &__list {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
        padding: 6px;
    }

    &__row {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 7px 8px;
        border-radius: 8px;
        cursor: pointer;
        user-select: none;
        transition: background 100ms ease;

        &:hover { background: var(--af-surface); }

        &.is-active {
            background: var(--af-surface-2);

            .fnp__chevron { color: var(--af-fg); }
        }

        &.is-checked .fnp__label { color: var(--af-fg); font-weight: 600; }
    }

    &__check {
        flex: 0 0 auto;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
        border-radius: 5px;
        border: 1.5px solid var(--af-border-strong);
        background: #fff;
        color: #fff;
        cursor: pointer;
        padding: 0;
        transition: background 100ms ease, border-color 100ms ease;

        svg { width: 11px; height: 11px; fill: currentColor; }

        &:hover { border-color: var(--af-primary); }
        &:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--af-primary-ring); }

        &.is-checked, &.is-indeterminate {
            background: var(--af-primary);
            border-color: var(--af-primary);
        }
    }

    &__label {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 13.5px;
        color: #344054;
    }

    &__hit {
        background: #fff3a3;
        border-radius: 3px;
        color: var(--af-fg);
    }

    &__meta {
        flex: 0 0 auto;
        font-size: 11.5px;
        font-weight: 600;
        color: var(--af-faint);
        font-variant-numeric: tabular-nums;
    }

    &__chevron {
        flex: 0 0 auto;
        width: 12px;
        height: 12px;
        fill: currentColor;
        color: var(--af-faint);
    }

    &__empty {
        padding: 36px 16px;
        text-align: center;
        font-size: 13px;
        color: var(--af-faint);
    }

    &__chips {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
    }

    &__chip {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        max-width: 100%;
        padding: 5px 8px 5px 10px;
        border-radius: 999px;
        border: 1px solid #fbcfe3;
        background: var(--af-primary-soft);
        color: var(--af-fg);
        font-size: 12.5px;
        cursor: pointer;
        transition: border-color 100ms ease, background 100ms ease;

        &:hover { border-color: var(--af-primary); }
        &:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--af-primary-ring); }
    }

    &__chip-label { font-weight: 600; white-space: nowrap; }

    &__chip-parent {
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--af-muted);

        &::before { content: '· '; }
    }

    &__chip-x { width: 12px; height: 12px; fill: currentColor; color: var(--af-muted); }

    &__chips-clear {
        border: 0;
        background: transparent;
        padding: 4px 6px;
        font-size: 12.5px;
        font-weight: 600;
        color: var(--af-muted);
        cursor: pointer;
        text-decoration: underline;
        text-underline-offset: 2px;

        &:hover { color: var(--af-primary); }
    }

    @media (max-width: 820px) {
        &__toolbar { flex-wrap: wrap; }
        &__cols { grid-template-columns: 1fr; }
        &__col {
            height: 200px;
            & + & { border-left: 0; border-top: 1px solid var(--af-border); }
        }
    }
}
</style>
